import { sanitize } from '@ben/common/dom';
import type { ITheme } from '@ben/orion/utils/theme';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { fsMaskForHSBCInstance } from '@ben/shared/constants';
import styled, { css, useTheme } from 'styled-components';

export interface IOptionWrapper {
  title: string;
  description?: string;
  borderColor?: string;
  children?: React.ReactNode;
  disabled?: boolean;
  hideBottomBorder?: boolean;
  noGap?: boolean;
  id?: string;
}

const OptionWrapper = ({
  children,
  title,
  description,
  borderColor,
  disabled = false,
  hideBottomBorder = false,
  noGap = false,
  id,
}: IOptionWrapper) => {
  const hasDescription = Boolean(description);
  const theme = useTheme();

  return (
    <Container
      borderColor={borderColor}
      hideBottomBorder={hideBottomBorder}
      noGap={noGap}
    >
      <TextContainer hasDescription={hasDescription} $disabled={disabled}>
        <Typography
          variant={TypographyVariant.h3}
          className={fsMaskForHSBCInstance}
        >
          {title}
        </Typography>
        {description && (
          <DescriptionTypography
            variant={TypographyVariant.bodyLg1}
            color={theme.palette.neutral[90]}
            dangerouslySetInnerHTML={{ __html: sanitize(description) }}
            id={id}
          />
        )}
      </TextContainer>
      {children}
    </Container>
  );
};

export default OptionWrapper;

const Container = styled('div')`
  ${({
    borderColor,
    disabled,
    hideBottomBorder,
    noGap,
    theme,
  }: {
    borderColor?: string;
    disabled?: boolean;
    hideBottomBorder?: boolean;
    noGap: boolean;
    theme: ITheme;
  }) => css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;

    width: 100%;
    padding: ${theme.spacing(3)};
    background: ${theme.palette.neutral[40]};
    border-radius: ${theme.borderRadius.sm};
    border: ${noGap
      ? `none`
      : `1px solid ${borderColor || theme.palette.neutral[60]}`};
    ${hideBottomBorder &&
    css`
      border-bottom: none;
      border-radius: 4px 4px 0 0;
    `}
  `}
`;

const TextContainer = styled('div')`
  ${({
    hasDescription,
    theme,
    $disabled,
  }: {
    hasDescription: boolean;
    theme: ITheme;
    $disabled: boolean;
  }) => css`
    width: 100%;
    height: 100%;
    overflow-x: hidden;
    word-wrap: break-word;
    margin-top: ${hasDescription ? theme.spacing(0) : theme.spacing('5px')};
    opacity: ${$disabled ? 0.4 : 1};
  `}
`;

const DescriptionTypography = styled(Typography)`
  ${({ theme }) => css`
    margin-top: ${theme.spacing(1)};

    a {
      text-decoration: underline;
      font-weight: 600;
      :focus-visible {
        border: 1px solid ${theme.palette.focus};
      }
    }
  `}
`;
